import { useEffect, useRef, useState } from "react"
import ScreenWrapper from "@/components/ScreenWrapper"
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router"
import {
  Button,
  DetailSidebar,
  downloadFromUrl,
  Input,
  LayoutWithSidebar,
  RadioBooleanField,
  Separator,
  useToast,
} from "toptaal-shared"
import SignatureCanvas from "react-signature-canvas"
import { TrashIcon } from "@heroicons/react/24/outline"
import {
  useSignStudentContractMutation,
  useStudentContractDocumentQuery,
  useStudentContractOpenedAtTimesMutation,
} from "students-api"
import { PDFViewer } from "@/components/PDFViewer"
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid"

export const Route = createFileRoute(
  "/$documentId/$contractUploadId/$contractName/document-ondertekenen",
)({
  component: RouteComponent,
})

function RouteComponent() {
  const router = useRouter()
  const [place, setPlace] = useState("")
  const [consentSatisfactionSurvey, setConsentSatisfactionSurvey] = useState<
    boolean | null
  >(null)
  const { success, error } = useToast()

  const { documentId, contractUploadId, contractName } = useParams({
    from: "/$documentId/$contractUploadId/$contractName/document-ondertekenen",
  })

  const sigRef = useRef<SignatureCanvas | null>(null)
  const [hasSigned, setHasSigned] = useState(false)

  const mutation = useSignStudentContractMutation()
  const appendOpenedAtTimesMutation = useStudentContractOpenedAtTimesMutation()

  useEffect(() => {
    appendOpenedAtTimesMutation.mutate(Number(documentId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId])

  const { data: contract } = useStudentContractDocumentQuery({
    studentContractId: Number(documentId),
    contractUploadId: Number(contractUploadId),
  })

  const clear = () => {
    sigRef.current?.clear()
    setHasSigned(false)
  }
  const handleSubmit = () => {
    if (!sigRef.current) return
    const signature = sigRef.current.toDataURL()
    mutation
      .mutateAsync({
        signature,
        studentContractId: Number(documentId),
        place,
        date: new Date().toLocaleDateString(),
        ...(consentSatisfactionSurvey !== null && {
          consentSatisfactionSurveyAt: consentSatisfactionSurvey
            ? new Date().toISOString()
            : null,
        }),
      })
      .then(() => {
        router.navigate({ to: "/home/contracten", replace: true })
        success("Contract succesvol ondertekend.")
      })
      .catch(() => {
        error(
          "Er is iets misgegaan bij het ondertekenen van het contract. Probeer het opnieuw.",
        )
      })
  }

  const onDownloadClick = () => {
    if (contract?.data?.url) {
      downloadFromUrl(contract.data.url, "contract.pdf")
    }
  }

  return (
    <ScreenWrapper className="!px-0">
      <LayoutWithSidebar
        rightPanelClassName="px-0 py-4"
        sidebar={
          <DetailSidebar goBackLabel="Document ondertekenen">
            <div className="space-y-3">
              <div>
                <div className="rounded-xl bg-white overflow-hidden w-full relative mb-8">
                  <SignatureCanvas
                    ref={(ref) => {
                      sigRef.current = ref
                    }}
                    penColor="#000000"
                    canvasProps={{
                      height: 250,
                      width: 450,
                    }}
                    onBegin={() => setHasSigned(true)}
                  />
                  <button
                    className="w-fit absolute right-4 top-4"
                    onClick={clear}
                  >
                    <TrashIcon className="size-5 text-gray-400 hover:text-primary-dark-blue transition-all duration-200 cursor-pointer" />
                  </button>
                </div>

                <Input
                  label="Plaats"
                  value={place}
                  onChange={(e) => setPlace(e.target.value)}
                />

                {contract?.data?.contractType === "general_contract" && (
                  <div className="mt-4">
                    <RadioBooleanField
                      label="Toestemming Panteia tevredenheidsonderzoek"
                      required
                      value={consentSatisfactionSurvey}
                      onChange={(value) =>
                        setConsentSatisfactionSurvey(value ?? null)
                      }
                    />
                  </div>
                )}

                <div className="flex gap-2 mt-8 justify-end">
                  <Button
                    className="w-fit"
                    onClick={handleSubmit}
                    disabled={
                      !hasSigned ||
                      place.trim() === "" ||
                      mutation.isPending ||
                      (contract?.data?.contractType === "general_contract" &&
                        consentSatisfactionSurvey === null)
                    }
                  >
                    Ondertekenen
                  </Button>
                </div>
              </div>
            </div>
          </DetailSidebar>
        }
      >
        <div className="flex justify-between px-8">
          {contractName}

          <ArrowDownTrayIcon
            className="size-5 hover:text-primary-green transition-colors duration-150 cursor-pointer mb-4"
            onClick={onDownloadClick}
          />
        </div>
        <Separator className="mb-4" />
        {contract && <PDFViewer file={contract.data.url} />}
      </LayoutWithSidebar>
    </ScreenWrapper>
  )
}
